import { Notice } from '@verlo/shared/components/ui/notice';
import { useState, useEffect } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { authApi } from '@verlo/shared/services/api';

// Roles that can authorise a floor override. Mirrors pin_auth.MANAGER_OVERRIDE_ROLES
// intersected with who holds `epos.override`. This only shapes the picker — the
// server is the one that actually enforces it (authorise_manager_pin).
const AUTHORISING_ROLES = new Set([
  'shift_lead', 'manager', 'general_manager', 'area_manager', 'operations', 'owner',
]);
const LEGACY_AUTHORISING_ROLES = new Set(['manager', 'admin', 'owner', 'super_admin']);

const canAuthorise = (u) =>
  AUTHORISING_ROLES.has((u.rbac_role || '').toLowerCase()) ||
  LEGACY_AUTHORISING_ROLES.has((u.role || '').toLowerCase());

/**
 * Manager-authorised override to clear an unpaid bill / void an order.
 *
 * The PIN is verified SERVER-SIDE (P5-MGRPIN / SEC-PIN-1): the manager names
 * themselves from the staff grid and enters their PIN, and the dialog calls
 * `POST /auth/pin/manager-authorise`, which verifies exactly one bcrypt hash and
 * audits the override. There is no client-side PIN literal any more — a valid
 * server response is the only thing that lets `onAuthorised` run.
 */
const ManagerOverrideDialog = ({
  open,
  onOpenChange,
  balance,
  siteId,
  onAuthorised,
  // When set, the dialog collects the manager + PIN and hands them to `onSubmit`
  // — the caller makes the guarded API call itself (pattern A). `onSubmit` may
  // throw an axios error; the dialog surfaces its detail. `onAuthorised` is the
  // older path: the dialog authorises against `/auth/pin/manager-authorise` and
  // the caller then runs an unguarded action.
  onSubmit,
  title = 'Manager authorisation',
  description,
  warning = 'This will void the current order and clear all unpaid items. This action is logged.',
  confirmLabel = 'Authorise',
  formatCurrency = (v) => `£${(v || 0).toFixed(2)}`,
}) => {
  const [managerId, setManagerId] = useState('');
  const [pin, setPin] = useState('');
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setManagerId('');
      setPin('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingManagers(true);
    authApi.pinStaffGrid(siteId)
      .then((res) => {
        if (!cancelled) setManagers((res.data?.users || []).filter(canAuthorise));
      })
      .catch(() => {
        if (!cancelled) setManagers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingManagers(false);
      });
    return () => { cancelled = true; };
  }, [open, siteId]);

  const noManagers = !loadingManagers && managers.length === 0;

  const submit = async () => {
    if (!managerId) {
      setError('Select the authorising manager');
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      setError('Enter the 4–6 digit manager PIN');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (onSubmit) {
        await onSubmit({ managerId, pin });
      } else {
        await authApi.managerAuthorise({ manager_id: managerId, manager_pin: pin });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) setError(detail || 'Manager PIN not recognised');
      else if (status === 423 || status === 429) setError(detail || 'Too many tries. Wait a moment, then try again.');
      else setError(detail || "Couldn't authorise that. Try again.");
      setPin('');
      setSubmitting(false);
      return;
    }
    // Success. Close first, then (for the older path) run the caller's business action.
    setSubmitting(false);
    setPin('');
    setManagerId('');
    onOpenChange(false);
    if (!onSubmit) await onAuthorised?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="override-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-signal-warn">
            <ShieldAlert size={18} />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>
            {description || `Enter a manager PIN to clear bill of ${formatCurrency(balance || 0)}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Notice tone="warn" word="Override">{warning}</Notice>

          <div className="space-y-1.5">
            <Label className="text-xs">Authorising manager</Label>
            <Select
              value={managerId}
              onValueChange={setManagerId}
              disabled={submitting || loadingManagers || noManagers}
            >
              <SelectTrigger className="h-10" data-testid="override-manager">
                <SelectValue
                  placeholder={
                    loadingManagers ? 'Loading…'
                      : noManagers ? 'No manager available on this site'
                      : 'Select manager'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="override-pin" className="text-xs flex items-center gap-1.5">
              <ShieldAlert size={11} className="text-signal-warn" />
              Manager PIN
            </Label>
            <Input
              id="override-pin"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              data-testid="override-pin-input"
              disabled={submitting || noManagers}
              autoComplete="off"
            />
            {error && (
              <p className="text-xs text-signal-stop text-center" data-testid="override-error">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="bg-signal-warn hover:bg-signal-warn gap-1.5"
            onClick={submit}
            disabled={submitting || noManagers || !managerId || pin.length < 4}
            data-testid="override-submit"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManagerOverrideDialog;
